// Three
import * as THREE from 'three';

// Classes
import Canvas from '../../../classes/Canvas';
import NodeEmitter from '../../../classes/NodeEmitter';

// Canvas Components
import Camera from './Camera';
import Gallery from './Gallery';
import GalleryControls from './GalleryControls';

// Dedicated WebGL scene for the portfolio. Scene + renderer come from Canvas.js.
// The DOM gallery (MultidimensionalGallery) keeps handling drag and moving its
// invisible figures; this experience reads those bounds each frame and renders
// matching textured planes. Lens distortion is done per-plane in the vertex
// shader (see GalleryItem), not as a screen-space post-process.
export default class PortfolioExperience extends Canvas {
	constructor(el, galleryComponent) {
		super(el); // '.webgl-portfolio'

		// transparent: only the planes paint; viewport bg/overlay show through gaps
		this.renderer.setClearColor(0x000000, 0);

		this.galleryComponent = galleryComponent;
		this.isReady = false;

		this.camera = new Camera({ sizes: this.sizes });
		this.scene.add(this.camera.el);

		// previous lerped scroll, for the per-frame "did anything move" delta
		this.prevScroll = { x: 0, y: 0 };

		// Render-on-demand: only redraw when something changed. Set true to force
		// one render (first frame after textures load, and after every resize).
		this._dirty = false;

		// Hard 60fps cap: on a 120Hz display the rAF fires ~every 8ms; rendering
		// every tick wastes GPU and can stutter under sustained scroll. Skip ticks
		// until at least one frame-interval has elapsed. Slack (~1.5ms below 16.67)
		// keeps true-60Hz displays from rounding down to 30fps.
		this._lastFrame = 0;
		this._frameInterval = 1000 / 60 - 1.5;
	}

	// Per-frame scroll delta of the DOM gallery's lerped position (`.scroll`),
	// used to gate render-on-demand (only redraw when the gallery actually moved).
	_step() {
		const s = this.galleryComponent && this.galleryComponent.scroll;
		let dx = 0;
		let dy = 0;
		if (s) {
			dx = s.x - this.prevScroll.x;
			dy = s.y - this.prevScroll.y;
			this.prevScroll.x = s.x;
			this.prevScroll.y = s.y;
		}
		return { dx, dy };
	}

	create() {
		this.gallery = new Gallery({
			scene: this.scene,
			sizes: this.sizes,
			galleryComponent: this.galleryComponent,
		});

		this.gallery.createItems().then(() => {
			this.galleryComponent.element.classList.add('multidim-gallery--webgl');
			// dev authoring + timeline (no-op unless URL hash is #debug)
			this.controls = new GalleryControls({
				gallery: this.gallery,
				items: this.gallery.items,
				camera: this.camera,
				uniforms: this.gallery.uniforms,
				galleryComponent: this.galleryComponent,
			});
			this._dirty = true; // force the first render now that planes exist
			this.isReady = true;
		});

		this.onResize();
	}

	update(time) {
		if (!this.isReady) return;

		// 60fps cap — bail before any work if we're ahead of the frame budget.
		// _step() is not called yet, so the skipped scroll delta is folded into
		// the next allowed frame (prevScroll only advances when we actually run).
		if (time - this._lastFrame < this._frameInterval) return;
		this._lastFrame = time;

		// Render-on-demand: redraw only when the gallery moved, the intro timeline
		// or #debug tools are live, a resize/load is pending, or the video is visible.
		const { dx, dy } = this._step();
		const tl = this.controls && this.controls.timeline;
		const timelineActive = !!(tl && tl.isActive());
		const debug = !!(this.controls && this.controls.active);
		const needsRender =
			dx !== 0 ||
			dy !== 0 ||
			timelineActive ||
			debug ||
			this._dirty ||
			this.gallery.isVideoOnScreen();
		if (!needsRender) return;

		// resize/first frame only: re-bake instances from layout data (heavy pass)
		if (this._dirty) this.gallery.applyLayout();
		// authored tf (intro / #debug) → instance attributes, only while animating
		if (timelineActive || debug) this.gallery.syncTf();
		// #debug: keep the selection/overlap overlay tracking the moving planes
		if (debug && this.controls.updateDebug) this.controls.updateDebug();
		this.gallery.update(); // shared scroll/scale uniforms, every render

		if (this.gallery.videoTexture) this.gallery.videoTexture.needsUpdate = true;

		this.renderer.render(this.scene, this.camera.el);
		NodeEmitter.emit('webglRender'); // FPSMeter counts these → "webgl" line
		this._dirty = false;
	}

	onResize() {
		super.onResize();
		this.camera.resizeCamera(this.sizes);
		if (this.gallery) this.gallery.onResize(this.sizes);
		this._dirty = true; // re-read bounds + re-bake layout + render next frame
	}

	show() {}
	hide() {}

	destroy() {
		if (this.galleryComponent && this.galleryComponent.element) {
			this.galleryComponent.element.classList.remove('multidim-gallery--webgl');
		}
		if (this.controls) this.controls.destroy();
		if (this.gallery) this.gallery.destroy();
		this.scene.traverse((child) => {
			if (child instanceof THREE.Mesh) child.geometry.dispose();
		});
		this.renderer.dispose();
		this.isReady = false;
	}
}
